import { useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

const Line = ({ label, value, tone = "" }) => (
  <div className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className={`tabular-nums font-semibold ${tone}`}>{value}</span>
  </div>
);

export const EncryptionRotationCard = () => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);

  const call = async (fn, label) => {
    setBusy(true);
    try {
      return await fn();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const checkStatus = async () => {
    const res = await call(() => api.get("/admin/encryption-status"), "Status check");
    if (res) {
      setStatus(res.data);
      if (!res.data.ready) toast.error("The new keys are missing from this environment");
    }
  };

  const run = async (apply) => {
    const res = await call(
      () => api.post(`/admin/rotate-encryption-keys${apply ? "?apply=true" : ""}`),
      apply ? "Migration" : "Dry run"
    );
    if (res) {
      setResult(res.data);
      toast[res.data.ok ? "success" : "error"](
        `${res.data.records_re_encrypted} records ${apply ? "re-encrypted" : "would be re-encrypted"}, ${res.data.failed_count} failed`
      );
      if (apply) checkStatus();
    }
  };

  return (
    <Card className="border-amber-500/30" data-testid="encryption-rotation-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {status?.rotation_complete ? (
            <ShieldCheck size={18} className="text-emerald-500" />
          ) : (
            <ShieldAlert size={18} className="text-amber-500" />
          )}
          Encryption key rotation
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          One-time job. Re-encrypts saved vault passwords and W-9 records onto the new keys. Safe to
          run twice — nothing is written unless it decrypts and verifies first.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={checkStatus} data-testid="encryption-status-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "1. Check status"}
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => run(false)} data-testid="encryption-dry-run-btn">
            2. Dry run
          </Button>
          <Button size="sm" disabled={busy} onClick={() => run(true)} data-testid="encryption-apply-btn">
            3. Run migration
          </Button>
        </div>

        {status && status.ready && (
          <div data-testid="encryption-status-output">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Where the data sits</p>
            <Line label="W-9 fields on new key" value={status.w9_fields_on_new_key} tone="text-emerald-500" />
            <Line label="Vault passwords on new key" value={status.vault_on_new_key} tone="text-emerald-500" />
            <Line
              label="Still on the old key"
              value={status.still_on_old_key}
              tone={status.still_on_old_key ? "text-amber-500" : "text-muted-foreground"}
            />
            <Line
              label="Unreadable"
              value={status.w9_fields_unreadable + status.vault_unreadable}
              tone={status.w9_fields_unreadable + status.vault_unreadable ? "text-rose-500" : "text-muted-foreground"}
            />
            {status.rotation_complete && (
              <p className="text-xs text-emerald-500 mt-2">
                Everything is on the new keys. The old keys can be retired.
              </p>
            )}
          </div>
        )}

        {status && !status.ready && (
          <p className="text-xs text-rose-500" data-testid="encryption-not-ready">{status.detail}</p>
        )}

        {result && (
          <div data-testid="encryption-result-output">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Last run · {result.mode === "applied" ? "applied" : "dry run"}
            </p>
            <Line label="Vault passwords" value={`${result.vault_passwords.rotated} / ${result.vault_passwords.total}`} />
            <Line
              label="W-9 records"
              value={`${result.w9_records.rotated} / ${result.w9_records.total} (${result.w9_records.fields_rotated} fields)`}
            />
            <Line
              label="Failed"
              value={result.failed_count}
              tone={result.failed_count ? "text-rose-500" : "text-emerald-500"}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
